import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiClient } from '@/shared/api'

// --- Types ---

export interface ChatbotTest {
  id: number
  company_id: number
  name: string
  description: string | null
  customer_prompt: string
  evaluation_prompt: string
  max_turns: number
  starting_agent: string
  has_interruption: boolean
  mock_tools?: Record<string, string>
  created_at: string
  updated_at: string
  test_runs?: Array<{ status: string; summary: { total: number; passed: number; failed: number; errors: number } | null }>
}

export interface ChatbotTestRun {
  id: string
  test_id: number
  llm_id: number
  status: string
  summary: { total: number; passed: number; failed: number; errors: number } | null
  started_at: string | null
  completed_at: string | null
  created_at: string
}

export interface ChatbotTestResult {
  id: number
  test_run_id: string
  conversation_id: string | null
  status: string
  evaluation: string | null
  error: string | null
  created_at: string
  completed_at: string | null
}

export interface ChatbotTestRunWithResults extends ChatbotTestRun {
  results: ChatbotTestResult[]
}

export interface ChatbotTestStats {
  passed: number
  failed: number
  running: number
  not_run: number
}

// --- Queries ---

export function useChatbotTests(campaignId: number | null) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['chatbot-tests'],
    queryFn: async () => {
      const result = await apiClient<{ tests: ChatbotTest[]; total: number }>(
        `/chatbot-tests?campaign_id=${campaignId}`
      )
      return result.tests
    },
    refetchInterval: (query) => {
      const tests = query.state.data
      if (tests?.some((t) => t.test_runs?.[0]?.status === 'running' || t.test_runs?.[0]?.status === 'pending')) {
        return 5000
      }
      return false
    },
  })
}

export function useChatbotTest(id: number | null) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['chatbot-test', id],
    queryFn: async () => {
      return apiClient<ChatbotTest>(`/chatbot-tests/${id}`)
    },
    enabled: !!id,
  })
}

export function useChatbotTestStats() {
  const api = useApiClient()

  return useQuery({
    queryKey: ['chatbot-test-stats'],
    queryFn: async () => {
      return api<ChatbotTestStats>('/chatbot-tests/stats')
    },
    refetchInterval: (query) => {
      const stats = query.state.data
      if (stats && stats.running > 0) {
        return 5000
      }
      return false
    },
  })
}

export function useChatbotTestRuns(testId: number | null) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['chatbot-test-runs', testId],
    queryFn: async () => {
      const result = await apiClient<{ runs: ChatbotTestRun[]; total: number }>(
        `/chatbot-tests/${testId}/runs`
      )
      return result.runs
    },
    enabled: !!testId,
    placeholderData: undefined,
    refetchInterval: (query) => {
      const runs = query.state.data
      if (runs?.some((r) => r.status === 'running' || r.status === 'pending')) {
        return 5000
      }
      return false
    },
  })
}

export function useChatbotTestRunDetail(testId: number | null, runId: string | null) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['chatbot-test-run', testId, runId],
    queryFn: async () => {
      return apiClient<ChatbotTestRunWithResults>(
        `/chatbot-tests/${testId}/runs/${runId}`
      )
    },
    enabled: !!testId && !!runId,
    placeholderData: undefined,
    refetchInterval: (query) => {
      const run = query.state.data
      if (run && (run.status === 'running' || run.status === 'pending')) {
        return 3000
      }
      return false
    },
  })
}

export function useLlmTools(llmId: number | null) {
  const api = useApiClient()

  return useQuery({
    queryKey: ['llm-tools', llmId],
    queryFn: async () => {
      if (!llmId) return []
      return api<Array<{ id: number; name: string; description: string | null }>>(
        `/chatbot-tests/llm/${llmId}/tools`
      )
    },
    enabled: !!llmId,
  })
}

// --- Mutations ---

export interface CreateChatbotTestInput {
  name: string
  description?: string
  customer_prompt: string
  evaluation_prompt: string
  max_turns?: number
  starting_agent?: string
  has_interruption?: boolean
  mock_tools?: Record<string, string>
}

export function useCreateChatbotTest() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateChatbotTestInput) => {
      return apiClient<ChatbotTest>('/chatbot-tests', {
        method: 'POST',
        body: JSON.stringify(input),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatbot-tests'] })
      queryClient.invalidateQueries({ queryKey: ['chatbot-test-stats'] })
    },
  })
}

export function useUpdateChatbotTest() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<CreateChatbotTestInput> & { id: number }) => {
      return apiClient<ChatbotTest>(`/chatbot-tests/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatbot-tests'] })
      queryClient.invalidateQueries({ queryKey: ['chatbot-test'] })
    },
  })
}

export function useDeleteChatbotTest() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      return apiClient<void>(`/chatbot-tests/${id}`, {
        method: 'DELETE',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatbot-tests'] })
      queryClient.invalidateQueries({ queryKey: ['chatbot-test-stats'] })
    },
  })
}

export function useCloneChatbotTest() {
  const api = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      return api<ChatbotTest>(`/chatbot-tests/${id}/clone`, {
        method: 'POST',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatbot-tests'] })
      queryClient.invalidateQueries({ queryKey: ['chatbot-test-stats'] })
    },
  })
}

export function useTriggerTestRun() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ testId, llmId }: { testId: number; llmId: number }) => {
      return apiClient<{ runId: string; status: string }>(
        `/chatbot-tests/${testId}/run`,
        {
          method: 'POST',
          body: JSON.stringify({ llm_id: llmId }),
        }
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatbot-tests'] })
      queryClient.invalidateQueries({ queryKey: ['chatbot-test-runs'] })
      queryClient.invalidateQueries({ queryKey: ['chatbot-test-stats'] })
      queryClient.invalidateQueries({ queryKey: ['last-test-result'] })
    },
  })
}

export function useImportFromQA() {
  const api = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (callId: string) => {
      return api<ChatbotTest>('/chatbot-tests/import-from-qa', {
        method: 'POST',
        body: JSON.stringify({ call_id: callId }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatbot-tests'] })
      queryClient.invalidateQueries({ queryKey: ['chatbot-test-stats'] })
    },
  })
}

export function useImportConversationFromQA() {
  const api = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (conversationId: string) => {
      return api<ChatbotTest>('/chatbot-tests/import-from-qa-conversation', {
        method: 'POST',
        body: JSON.stringify({ conversation_id: conversationId }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatbot-tests'] })
      queryClient.invalidateQueries({ queryKey: ['chatbot-test-stats'] })
    },
  })
}

// --- Last test result (single endpoint) ---

interface LastTestResultData {
  run: { id: string; status: string; summary: Record<string, number> | null; created_at: string } | null
  result: { id: number; status: string; conversation_id: string | null; evaluation: string | null; error: string | null } | null
  messages: any[]
}

export function useLastTestResult(testId: number | null) {
  const api = useApiClient()

  return useQuery({
    queryKey: ['last-test-result', testId],
    queryFn: async () => {
      return api<LastTestResultData>(`/chatbot-tests/${testId}/last-result`)
    },
    enabled: !!testId,
    gcTime: 0,
    staleTime: 0,
    refetchInterval: (query) => {
      const data = query.state.data
      if (data?.result?.status === 'running' || data?.run?.status === 'running' || data?.run?.status === 'pending') {
        return 5000
      }
      return false
    },
  })
}

// --- Conversation messages ---

export function useConversationMessages(testId: number | null, conversationId: string | null) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['conversation-messages', testId, conversationId],
    queryFn: async () => {
      return apiClient<any[]>(
        `/chatbot-tests/${testId}/conversations/${conversationId}/messages`,
      )
    },
    enabled: !!testId && !!conversationId,
    gcTime: 0,
    staleTime: 0,
  })
}